import { BOOKING_TYPE } from "src/store/booking";
import { apiClient, apiClientAuth, charterApiClient } from "./axios-config";

export const localeApi = {
  getStringResources: (locale) => {
    return apiClient.request({
      method: "GET",
      url: `/resources/category/resources/${locale}`,
    });
  },
  getAdditionalStringResources: (locale) => {
    return apiClient.request({
      method: "GET",
      url: `/resources/category/additional_services/${locale}`,
    });
  },
  getFAQResources: (locale) => {
    return apiClient.request({
      method: "GET",
      url: `/resources/category/faq/${locale}`,
      headers: { "X-Company-Name": "default" }, // faq is only linekd to default tenant
    });
  },
};

export const companyProfileApi = {
  getCompanyProfileData: (companyId) => {
    return apiClient.request({
      method: "GET",
      url: `/tenant/name/${companyId}`,
    });
  },
};

export const airportsApi = {
  getAirports: (query, locale) => {
    return apiClient.request({
      method: "GET",
      url: `/airport/search?locale=${locale}&${query}`,
    });
  },
  getCityDetails: (iata) => {
    return apiClient.request({
      method: "GET",
      url: `/airport/iata/${iata}`,
    });
  },
  getPopularDestinationAirports: (iata) => {
    let url = `/destination/source`;
    url += iata ? `/${iata}` : "";
    return apiClient.request({
      method: "GET",
      url: url,
    });
  },
  getPopularArrivalAirports: (iata) => {
    let url = `/destination/popular`;
    url += iata ? `/${iata}` : "";
    return apiClient.request({
      method: "GET",
      url: url,
    });
  },
};

export const resultsApi = {
  getSearchResultsKey: (query) => {
    return apiClient.request({
      method: "GET",
      url: `/v2/search${query}`,
      timeout: 3000,
      timeoutErrorMessage: "Search request timed out (3000ms).",
    });
  },
};

export const additionalServicesApi = {
  getServices: () => {
    return apiClient.request({
      method: "GET",
      url: `/services/all`,
    });
  },
};

export const ancillaryServicesApi = {
  getServices: (carrierCode) => {
    return apiClient.request({
      method: "GET",
      url: `/luggage/code/${carrierCode}`,
    });
  },
};

export const bookingOrderApi = {
  startBooking: (travelPackage, bookingType, bookingTypeData) => {
    const data = { ...travelPackage };
    if (bookingType) data.bookingType = bookingType;
    if (bookingType === BOOKING_TYPE.HOTEL && bookingTypeData) {
      data[bookingType] = bookingTypeData;
    }

    console.log(data);
    return apiClient.request({
      method: "POST",
      data: data,
      url: `/booking/create`,
    });
  },
  getOrderById: (orderId) => {
    return apiClientAuth.request({
      method: "GET",
      url: `/booking/retrieve/${orderId}`,
    });
  },
  getUserTrips: (userId) => {
    return apiClientAuth.request({
      method: "GET",
      url: `/booking/user/${userId}`,
    });
  },
  setHotelReservationPaymentType: (correlationId, type) => {
    return apiClientAuth.request({
      method: "POST",
      url: `/booking/hotelReservation/setPaymentType/${correlationId}?type=${type}`,
    });
  },
  sendGroupBookingRequest: (groupType, data) => {
    return apiClient.request({
      method: "POST",
      data: data,
      url: `/booking/addGroupBooking/${groupType}`,
    });
  },
  getOrderItems: (orderId) => {
    return apiClientAuth.request({
      method: "GET",
      url: `/booking/getBookingAndCommit/${orderId}`,
    });
  },
  batchUpdate: (updates, orderId, cancelToken) => {
    return apiClientAuth.request({
      method: "POST",
      data: updates,
      url: `/booking/batchUpdate/${orderId}`,
      cancelToken,
    });
  },
};

export const paymentApi = {
  getPaymentMethods: () => {
    return apiClient.request({
      method: "GET",
      url: "/payment/methods",
    });
  },
  processPaymentOrder: (payload) => {
    return apiClient.request({
      method: "POST",
      data: { payload },
      headers: {
        "Content-Type": "application/json",
      },
      url: "/payment/order",
    });
  },
  checkPaymentOrderToken: (token) => {
    return apiClient.request({
      method: "POST",
      url: "/payment/validate",
      data: { token },
    });
  },
};

export const staticApi = {
  getStaticBanners: (language) => {
    return apiClient.request({
      method: "GET",
      url: `/static/links/banners/default/${language}`,
    });
  },
  getStaticFiles: async (company, language, folderName) => {
    return apiClient
      .request({
        method: "GET",
        url: `/static/links/${folderName}/${company}/${language}`,
      })
      .then(async (res) => {
        if (!Array.isArray(res.data) || res.data.length === 0) {
          throw new Error(
            "Invalid data structure: expected an array with at least one element.",
          );
        }

        const data = {};
        const promises = [];
        res.data.forEach(async (targetLink) => {
          if (!targetLink) throw new Error("No target link found.");

          const ext = targetLink.split(".").pop();
          if (ext === "css" || ext === "html") {
            const promise = apiClient
              .request({
                method: "GET",
                url: targetLink,
                headers: {
                  "Content-Type": `text/${ext}`,
                },
                responseType: "text",
              })
              .then((res) => {
                data[ext] = res.data;
              })
              .catch((err) => {
                console.log(err);
                throw err;
              });
            promises.push(promise);
          }
        });
        await Promise.all(promises);
        return data;
      })
      .catch((err) => {
        console.log(err);
        if (err.response && err.response.status === 404) {
          return { html: "", css: "" };
        } else {
          throw err;
        }
      });
  },
};

export const fileApi = {
  getInvoice: (bookingNumber) => {
    return apiClient.request({
      method: "GET",
      url: `/invoice/generate/${bookingNumber}`,
      headers: {
        "Content-Type": "application/pdf",
      },
      responseType: "blob",
    });
  },
  getUserFile: (url) => {
    return apiClientAuth.request({
      method: "GET",
      url,
      responseType: "blob",
    });
  },
};

export const ticketApi = {
  getDocuments(correlationId) {
    return apiClientAuth.request({
      method: "GET",
      url: `/ticket/otherDocument/booking/${correlationId}`,
    });
  },
};

export const destinationsApi = {
  // Durable tenant-scoped package offer cache (cheapest per destination, max 21, ascending).
  getTravelDeals: (tenant) => {
    return apiClient.request({
      method: "GET",
      url: "/destination/offers",
      params: { tenant },
    });
  },
  // Legacy reference catalog without prices; used as fallback when the offer cache is empty.
  getLegacyTravelDeals: () => {
    return apiClient.request({
      method: "GET",
      url: "/destination/retrieve_offers",
    });
  },
};

// Public, unauthenticated read — same pattern as destinationsApi.getLegacyTravelDeals (a plain
// `apiClient` GET against a `@Secured(IS_ANONYMOUS)` backend endpoint). The admin-side CRUD for
// this same entity lives in avion-admin's api-charter-deals.js, talking to the same `/charter/*`
// backend — see avion-admin/docs/charter-deals-api-spec.md for the full contract.
export const charterApi = {
  getCharterDeals: () => {
    return charterApiClient.request({
      method: "GET",
      url: "/charter/retrieve_deals",
    });
  },
};

export const calendarApi = {
  getDirectFlightDates: (origins, destinations) =>
    apiClient.request({
      method: "GET",
      url: `/calendar/${origins.join(",")}/${destinations.join(",")}`,
    }),
};

export const statisticsApi = {
  getCheapFlights: (type, s_iata, d_iata, min, max, start, end, summary) => {
    return apiClient.request({
      method: "GET",
      url: "/statistics/cheap-flights",
      params: { type, s_iata, d_iata, min, max, start, end, summary },
    });
  },
};

export const promoApi = {
  validateCode: (code) => {
    return apiClient.request({
      method: "GET",
      url: `/promo/validation/${code}`,
    });
  },
};

export const toursApi = {
  getTours: () => {
    return apiClient.request({
      method: "GET",
      url: "/bus/admin/atg/tours",
    });
  },
  getTourByCode: (code) => {
    return apiClient.request({
      method: "GET",
      url: `/bus/admin/atg/tours/${code}`,
    });
  },
};

export const hotelsApi = {
  getPackageFlights: (correlationId) => {
    return apiClient.request({
      method: "POST",
      url: `/ratehawk/getFlightsBySearchRequest/${correlationId}`,
    });
  },
  getHotelPreview: (hid, requestKey) => {
    // //TODO: simulate error
    // return new Promise((resolve, reject) => {
    //   resolve(data);
    // });

    return apiClient.request({
      method: "GET",
      url: "/ratehawk/prebook/" + hid + "/" + requestKey,
      // RateHawk permits the upstream hotel provider to process prebook for up to 120 seconds.
      timeout: 130000,
    });
  },
  getRateDetails: (hid, rateId, requestKey) => {
    return apiClient.request({
      method: "GET",
      url: `/ratehawk/rate/${hid}/${requestKey}/${rateId}`,
    });
  },
  getCheapHotels: () => {
    return apiClient.request({
      method: "GET",
      url: "/ratehawk/offers/cheap",
    });
  },
  // Flight + hotel package deals for "Avio ceļojumi" — NOT the same endpoint as getCheapHotels
  // above (that one is hotel-only). See RATEHAWK_PACKAGE_UI_INSTRUCTIONS.
  getCheapPackages: (params) => {
    return apiClient.request({
      method: "GET",
      url: "/ratehawk/offers/cheap/packages",
      params,
    });
  },
};
